import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Ear, MessagesSquare, Clock, ArrowRight, Crown, CheckCircle2,
  CalendarClock, Timer, UserCheck, Coins, ShieldOff, Target, ScrollText,
} from 'lucide-react';
import LegalFooter from '../components/LegalFooter';
import AperitivoSection from '../components/AperitivoSection';
import { useLandingMetadata } from '../hooks/useLandingMetadata';

/**
 * Nymia · Landing "Operatori Call Center & Assistenza Clienti" — /call
 * Campaign-only page (Meta/Google/LinkedIn/Indeed Ads): NOT linked in any menu.
 * Dark & Gold, cinematic, mobile-first. Copy fornito dal founder (IT only).
 * Tono empatico e positivo — nessuna denigrazione del settore call center.
 */

const GOLD = '#D4AF37';
const IMG = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/70cab226-6488-47ae-8a5f-b42665648727/images/092fa5e74610e58cf5a5e7c16a5a9269cc9b9801a2f293b5b567f98226951133.jpeg',
  freedom: 'https://static.prod-images.emergentagent.com/jobs/70cab226-6488-47ae-8a5f-b42665648727/images/60a74da678469df7c271c1687a0f4153f8b19c4b1f4be96d220c641de6e5e325.jpeg',
  person: 'https://static.prod-images.emergentagent.com/jobs/70cab226-6488-47ae-8a5f-b42665648727/images/a65409c3f6870f32f3535f2930196af1c1f704de2abbf61384662cf9ef231f56.jpeg',
};

const easeCine = [0.16, 1, 0.3, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: easeCine },
  }),
};

const Serif = ({ children, className = '' }) => (
  <span className={className} style={{ fontFamily: "'Playfair Display', serif" }}>{children}</span>
);

const CtaButton = ({ testId, className = '' }) => (
  <Link
    to="/register"
    className={`inline-flex items-center gap-2.5 rounded-full px-8 py-4 text-base font-bold text-black shadow-[0_8px_40px_rgba(212,175,55,0.35)] transition-transform hover:scale-[1.03] sm:px-10 sm:text-lg ${className}`}
    style={{ backgroundColor: GOLD }}
    data-testid={testId}
  >
    Diventa una Voce Pro Nymia
    <ArrowRight className="h-5 w-5" />
  </Link>
);

const Header = () => (
  <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl" data-testid="call-header">
    <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
      <Link to="/" className="flex items-center gap-2.5" data-testid="call-logo">
        <Serif className="text-2xl font-semibold leading-none">
          <span style={{ color: GOLD }}>N</span>
        </Serif>
        <span className="text-sm font-semibold uppercase tracking-[0.35em] text-white">Nymia</span>
      </Link>
      <div className="flex items-center gap-2.5">
        <Link
          to="/login"
          className="rounded-full border border-white/15 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:border-white/40 sm:px-5"
          data-testid="call-login-btn"
        >
          Accedi
        </Link>
        <Link
          to="/register"
          className="rounded-full px-4 py-2 text-[13px] font-bold text-black transition-opacity hover:opacity-90 sm:px-5"
          style={{ backgroundColor: GOLD }}
          data-testid="call-register-btn"
        >
          Registrati
        </Link>
      </div>
    </div>
  </header>
);

export default function CallLandingPage() {
  useLandingMetadata('call');
  return (
    <div className="min-h-screen bg-[#050505] text-white" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="call-landing-page">
      <Header />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden pt-16" data-testid="call-hero">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className="h-full w-full object-cover object-center opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/40 to-[#050505]" />
        </div>
        <div className="relative mx-auto w-full max-w-4xl px-5 py-20 text-center">
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="mb-5 text-[11px] font-semibold uppercase tracking-[0.4em]" style={{ color: GOLD }}>
            Per chi lavora nell'assistenza clienti
          </motion.p>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="text-4xl leading-[1.12] sm:text-5xl lg:text-6xl" data-testid="call-hero-title">
            <Serif>Per anni hai seguito un copione.<br />Adesso <span style={{ color: GOLD }}>scrivi il tuo</span>.</Serif>
          </motion.h1>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mx-auto mt-7 max-w-2xl space-y-3 text-base leading-relaxed text-white/75 md:text-lg">
            <p className="font-semibold text-white/90">
              Hai passato anni parlando con le persone.<br />
              Hai gestito clienti difficili. Hai imparato ad ascoltare. Hai risolto problemi.
            </p>
            <p>Ora puoi utilizzare tutta la tua esperienza in un luogo dove contano <Serif className="italic" >le persone</Serif>, non gli script.</p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-10">
            <CtaButton testId="call-hero-cta" />
          </motion.div>
        </div>
      </section>

      {/* ── CONOSCI IL VALORE DI UNA CONVERSAZIONE ───────────── */}
      <section className="mx-auto max-w-6xl px-5 py-20 md:py-28" data-testid="call-valore">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="mb-12 text-center text-3xl sm:text-4xl">
          <Serif>Conosci il valore di una <span style={{ color: GOLD }}>conversazione</span></Serif>
        </motion.h2>
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { icon: Ear, title: 'Sai ascoltare', text: 'Ogni giorno hai parlato con decine di persone diverse. Hai imparato a comprendere bisogni, emozioni e situazioni molto differenti.' },
            { icon: MessagesSquare, title: 'Sai comunicare', text: 'La comunicazione è il tuo lavoro.', extra: 'Su Nymia questa capacità diventa il tuo valore.' },
            { icon: Clock, title: 'Libertà', text: 'Nessun turno imposto. Nessun supervisore. Nessun timer.', extra: 'Decidi tu quando collegarti. Decidi tu quanto tempo dedicare.' },
          ].map((c, i) => (
            <motion.div key={c.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-colors duration-300 hover:border-[#D4AF37]/40"
              data-testid={`call-card-${i}`}>
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: 'rgba(212,175,55,0.12)' }}>
                <c.icon className="h-6 w-6" style={{ color: GOLD }} strokeWidth={1.6} />
              </div>
              <h3 className="mb-3 text-xl font-semibold"><Serif>{c.title}</Serif></h3>
              <p className="text-sm leading-relaxed text-white/70">{c.text}</p>
              {c.extra && <p className="mt-3 text-sm italic leading-relaxed" style={{ color: GOLD }}>{c.extra}</p>}
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── SEZIONE EMOZIONALE ───────────────────────────────── */}
      <section className="border-y border-white/5 bg-white/[0.02] py-20 md:py-28" data-testid="call-emozionale">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 md:grid-cols-2">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <img src={IMG.person} alt="Un momento di calma" className="w-full rounded-2xl object-cover shadow-2xl shadow-black/60" loading="lazy" />
          </motion.div>
          <div>
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="mb-7 text-3xl leading-tight sm:text-4xl">
              <Serif>Per anni hai lavorato seguendo regole <span style={{ color: GOLD }}>che non erano le tue</span>.</Serif>
            </motion.h2>
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
              className="space-y-4 text-[15px] leading-relaxed text-white/70 md:text-base">
              <p>Hai seguito procedure. Hai rispettato tempi. Hai raggiunto obiettivi.<br />Hai gestito reclami. Hai affrontato clienti arrabbiati.</p>
              <p>Spesso con poco riconoscimento e sotto pressione.</p>
              <p className="text-lg font-semibold text-white/90"><Serif>Su Nymia succede il contrario.</Serif></p>
              <p>Qui non devi convincere nessuno. Non devi vendere nulla. Non hai uno script.</p>
              <p className="italic" style={{ color: GOLD }}>
                Hai semplicemente una persona dall'altra parte che ha scelto di parlare con te.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FINALMENTE SCEGLI TU ─────────────────────────────── */}
      <section className="py-20 md:py-28" data-testid="call-scegli-tu">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 md:grid-cols-2">
          <div className="order-2 md:order-1">
            <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="mb-8 text-3xl sm:text-4xl">
              <Serif>Finalmente <span style={{ color: GOLD }}>scegli tu</span></Serif>
            </motion.h2>
            <div className="space-y-3.5">
              {[
                { icon: CalendarClock, text: 'Quando collegarti.' },
                { icon: Timer, text: 'Quanto parlare.' },
                { icon: UserCheck, text: 'Con chi parlare.' },
                { icon: Coins, text: 'Quanto vale il tuo tempo.' },
                { icon: ShieldOff, text: 'Senza turni.' },
                { icon: Target, text: 'Senza obiettivi imposti.' },
                { icon: ScrollText, text: 'Senza copioni.' },
              ].map((r, i) => (
                <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i * 0.6}
                  className="flex items-center gap-4 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(212,175,55,0.12)' }}>
                    <r.icon style={{ color: GOLD, width: 18, height: 18 }} strokeWidth={1.8} />
                  </span>
                  <p className="text-[15px] font-medium text-white/85">{r.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative order-1 md:order-2">
            <img src={IMG.freedom} alt="La tua libertà" className="w-full rounded-2xl object-cover shadow-2xl shadow-black/60" loading="lazy" />
            <div className="absolute -bottom-4 -left-4 hidden rounded-2xl border border-[#D4AF37]/30 bg-[#050505] px-5 py-3 md:block">
              <p className="text-xs uppercase tracking-widest text-white/50">Il tuo tempo</p>
              <p className="text-lg font-semibold" style={{ color: GOLD }}><Serif>lo gestisci tu</Serif></p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SEZIONE IMPATTO ──────────────────────────────────── */}
      <section className="bg-black py-24 md:py-36" data-testid="call-impatto">
        <div className="mx-auto max-w-3xl space-y-8 px-5 text-center md:space-y-10">
          <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="text-3xl leading-snug text-white/85 sm:text-4xl lg:text-5xl">
            <Serif>Non sei un operatore.</Serif>
          </motion.p>
          <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1.5}
            className="text-4xl sm:text-5xl lg:text-6xl">
            <Serif>Sei <span style={{ color: GOLD }}>una persona</span>.</Serif>
          </motion.p>
          <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3}
            className="text-xl italic text-white/70 sm:text-2xl">
            <Serif>Ed è proprio questo il tuo valore.</Serif>
          </motion.p>
        </div>
      </section>

      {/* ── APERITIVO NYMIA (gestito da Admin) ───────────────── */}
      <AperitivoSection source="call" />

      {/* ── PERCHÉ ENTRARE ADESSO ────────────────────────────── */}
      <section className="relative overflow-hidden border-y border-[#D4AF37]/20 py-20 md:py-28" data-testid="call-adesso"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.10) 0%, rgba(5,5,5,0) 60%)' }}>
        <div className="mx-auto max-w-3xl px-5 text-center">
          <motion.span variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em]"
            style={{ color: GOLD }} data-testid="call-early-badge">
            <Crown className="h-3.5 w-3.5" /> Early Voce Pro
          </motion.span>
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="mb-7 text-3xl leading-tight sm:text-4xl lg:text-5xl">
            <Serif>Le prime Voci Pro costruiscono <span style={{ color: GOLD }}>il proprio spazio</span>.</Serif>
          </motion.h2>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
            className="space-y-4 text-[15px] leading-relaxed text-white/70 md:text-base">
            <p>Ogni nuova piattaforma attraversa una fase iniziale in cui è possibile costruire la propria presenza con meno concorrenza.</p>
            <p>Chi arriva oggi ha più tempo per farsi conoscere, creare fiducia, ricevere le prime recensioni e costruire un rapporto di fiducia con i primi utenti.</p>
            <p className="font-semibold text-white/90">Essere tra i primi significa avere più opportunità di crescere insieme alla piattaforma.</p>
          </motion.div>
        </div>
      </section>

      {/* ── NESSUN ALGORITMO ─────────────────────────────────── */}
      <section className="py-24 md:py-36" data-testid="call-algoritmo">
        <div className="mx-auto max-w-3xl px-5 text-center">
          {['Nessun algoritmo.', 'Nessun copione.'].map((line, i) => (
            <motion.p key={line} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="text-3xl leading-snug text-white/85 sm:text-4xl lg:text-5xl">
              <Serif>{line}</Serif>
            </motion.p>
          ))}
          <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3}
            className="mt-8 text-4xl sm:text-5xl lg:text-6xl">
            <Serif><span style={{ color: GOLD }}>Solo persone.</span></Serif>
          </motion.p>
        </div>
      </section>

      {/* ── SEZIONE FINALE ───────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-5 pb-8 md:pb-12" data-testid="call-finale">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="mb-7 text-center text-3xl leading-tight sm:text-4xl">
          <Serif>La tua esperienza vale molto più <span style={{ color: GOLD }}>di uno script</span>.</Serif>
        </motion.h2>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-9">
          <div className="space-y-3.5 text-[15px] leading-relaxed text-white/75 md:text-base">
            <p className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: GOLD }} strokeWidth={1.8} />
              Per anni hai imparato a gestire ogni tipo di conversazione. Questa esperienza ha un valore enorme.
            </p>
            <p className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: GOLD }} strokeWidth={1.8} />
              Su Nymia puoi utilizzarla in un ambiente dove le persone scelgono liberamente di parlare con te,
              senza pressioni commerciali, senza obiettivi di vendita e senza dover seguire un copione.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── CTA FINALE ───────────────────────────────────────── */}
      <section className="relative overflow-hidden py-20 md:py-28" data-testid="call-final-cta">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(212,175,55,0.12) 0%, rgba(5,5,5,0) 65%)' }} />
        <div className="relative mx-auto max-w-3xl px-5 text-center">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="text-3xl leading-tight sm:text-4xl lg:text-5xl">
            <Serif>È il momento di utilizzare la tua esperienza <span style={{ color: GOLD }}>in modo diverso</span>.</Serif>
          </motion.h2>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} className="mt-10">
            <CtaButton testId="call-final-cta-btn" />
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <footer className="border-t border-white/5 px-5 pb-10 pt-10" data-testid="call-footer">
        <LegalFooter className="mx-auto max-w-3xl" />
      </footer>
    </div>
  );
}
